import { Component, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Domain } from '@monsido/modules/models/api/domain';
import { CustomerInterface } from '@monsido/modules/models/api/interfaces/customer.interface';
import { DomainExportService } from '../../services/domain-export.service';
import { DomainManagementSharedService } from '../../services/domain-management-shared.service';
import { DomainActionProgress } from '../../types/domain-management.types';
import { Subscription } from 'rxjs';

@Component({
    selector: 'mon-export-selected-button',
    templateUrl: './export-selected-button.component.html',
    standalone: false,
})
export class ExportSelectedButtonComponent implements OnDestroy {
    @Input() selectedDomains: Domain[] = [];
    @Input() customer: CustomerInterface;
    @Input() disabled: boolean = false;

    @Output() exportStarted = new EventEmitter<void>();
    @Output() exportCompleted = new EventEmitter<void>();
    @Output() exportFailed = new EventEmitter<string>();

    isExporting = false;
    progress: DomainActionProgress = { current: 0, total: 0, message: '', isActive: false };
    private progressSubscription?: Subscription;

    constructor (
        private domainExportService: DomainExportService,
        private sharedService: DomainManagementSharedService,
    ) {}

    ngOnDestroy (): void {
        if (this.progressSubscription) {
            this.progressSubscription.unsubscribe();
        }
    }

    get isDisabled (): boolean {
        return this.disabled || this.selectedDomains.length === 0 || this.isExporting;
    }

    async exportSelected (): Promise<void> {
        if (!this.sharedService.validateDomainsSelection(this.selectedDomains)) {
            return;
        }

        if (this.isExporting) {
            return;
        }

        const confirmed = await this.sharedService.showConfirmation(
            this.sharedService.createSelectedExportConfirmation(this.selectedDomains),
            'Export Selected Domains',
        );

        if (!confirmed) {
            return;
        }

        try {
            this.isExporting = true;
            this.progress = { current: 0, total: this.selectedDomains.length, message: 'Preparing export...', isActive: true };
            this.exportStarted.emit();

            const progressTracker = this.sharedService.createProgressTracker(
                this.domainExportService.progress$,
                (progress) => {
                    this.progress = progress;
                },
            );
            this.progressSubscription = progressTracker.subscribe();

            const sanitizedCustomerName = this.sharedService.sanitizeCustomerName(this.customer);
            await this.domainExportService.exportSelectedDomains(this.selectedDomains, sanitizedCustomerName);

            this.sharedService.showSuccessMessage(
                'Successfully exported [[count]] domains',
                { count: this.selectedDomains.length },
            );

            this.exportCompleted.emit();

        } catch (error) {
            const errorMessage = error?.message || 'Unknown error';
            this.sharedService.showErrorMessage(
                'Failed to export domains: [[error]]',
                { error: errorMessage },
            );
            this.exportFailed.emit(errorMessage);
        } finally {
            this.isExporting = false;
            this.progress.isActive = false;
            if (this.progressSubscription) {
                this.progressSubscription.unsubscribe();
                this.progressSubscription = undefined;
            }
            this.domainExportService.resetProgress();
        }
    }
}
